import { Event, GameState, EndingType } from '../core/types';
import { EventResult } from '../core/types';

/**
 * UI渲染器类
 * 负责游戏界面的绘制和用户交互
 */
export class UIRenderer {
  private gameContainer: HTMLElement;
  private loadingScreen: HTMLElement;
  private gameScreen: HTMLElement;
  private eventContainer: HTMLElement | null = null;
  private statusBar: HTMLElement | null = null;
  private choicesContainer: HTMLElement | null = null;
  private loadingProgress: HTMLElement | null = null;
  private resultContainer: HTMLElement | null = null;
  private messageOverlay: HTMLElement | null = null;
  private persistentMessageElement: HTMLElement | null = null;
  
  // 进度条元素引用
  private progressContainerElement: HTMLElement | null = null;
  private homeIconElement: HTMLElement | null = null;
  private routeLineElement: HTMLElement | null = null;
  private personIconElement: HTMLElement | null = null;
  private destinationIconElement: HTMLElement | null = null;
  
  // 回调函数
  private onChoiceSelectedCallback: ((choiceId: string) => void) | null = null;
  private onGameStartCallback: (() => void) | null = null;
  private onGameRestartCallback: (() => void) | null = null;

  private goalDescriptions: string[] = [
    "今日挑战：在老板发现你不在工位前，光速打卡！9点前！",
    "9点！打卡的deadline，也是你薪水的生命线！冲啊！",
    "警报！警报！还有时间抵达战场（公司），9点前消灭打卡机！",
    "你的任务，勇敢的打工人：9点前攻占公司打卡机！为了全勤奖！",
    "保持微笑，奔向公司！目标：9点前！毕竟，迟到一分钟，钱包痛一天！",
    "别磨蹭了，内卷的同事已经在工位开始摸鱼了！9点前到公司！",
    "前方高能预警：距离9点打卡还有倒计时1小时！快跑！"
  ];

  /**
   * 构造函数
   * @param containerId 容器ID
   */
  constructor(containerId: string = 'app') {
    this.gameContainer = document.getElementById(containerId) || document.body;
    
    // 获取现有的屏幕元素
    this.loadingScreen = document.getElementById('loading-screen') || this.createLoadingScreen();
    this.gameScreen = document.getElementById('game-screen') || this.createGameScreen();
    
    // 初始化加载进度条
    this.loadingProgress = this.loadingScreen.querySelector('.loading-progress');
    
    // 隐藏游戏屏幕，显示加载屏幕
    this.showScreen(this.loadingScreen);
    this.hideScreen(this.gameScreen);
  }

  /**
   * 创建加载屏幕
   */
  private createLoadingScreen(): HTMLElement {
    const screen = document.createElement('div');
    screen.id = 'loading-screen';
    screen.className = 'screen';
    
    screen.innerHTML = `
      <h1 class="hand-drawn">上班路上</h1>
      <p class="subtitle">打工人的日常冒险</p>
      <div class="loading-bar">
        <div class="loading-progress"></div>
      </div>
    `;
    
    this.gameContainer.appendChild(screen);
    return screen;
  }

  /**
   * 创建游戏屏幕
   */
  private createGameScreen(): HTMLElement {
    const screen = document.createElement('div');
    screen.id = 'game-screen';
    screen.className = 'screen hidden';
    
    this.gameContainer.appendChild(screen);
    return screen;
  }

  /**
   * 初始化游戏UI
   */
  initializeGameUI(): void {
    // 清空游戏屏幕
    this.gameScreen.innerHTML = '';
    // 应用game-screen的核心布局样式
    this.gameScreen.style.display = 'flex';
    this.gameScreen.style.flexDirection = 'column';
    this.gameScreen.style.height = '100%'; // 依赖父容器已设100%或100vh
    
    // 重置进度条相关元素的引用，以便在updateStatusBar中重建它们
    this.progressContainerElement = null;
    this.homeIconElement = null;
    this.routeLineElement = null;
    this.personIconElement = null;
    this.destinationIconElement = null;
    
    // 创建状态栏
    this.statusBar = document.createElement('div');
    this.statusBar.className = 'status-bar';
    this.gameScreen.appendChild(this.statusBar);
    
    // 创建消息覆盖层
    this.messageOverlay = document.createElement('div');
    this.messageOverlay.className = 'message-overlay';
    this.gameScreen.appendChild(this.messageOverlay);
    
    // 创建持久化消息元素 (New)
    this.persistentMessageElement = document.createElement('div');
    this.persistentMessageElement.className = 'overlay-message persistent-message hidden';
    if (this.messageOverlay) {
        this.messageOverlay.appendChild(this.persistentMessageElement);
    }

    // 创建主内容区域容器
    const mainContentContainer = document.createElement('div');
    mainContentContainer.style.display = 'flex';
    mainContentContainer.style.flexDirection = 'column';
    mainContentContainer.style.flex = '1';
    mainContentContainer.style.overflow = 'hidden';
    
    // 创建事件容器
    this.eventContainer = document.createElement('div');
    this.eventContainer.className = 'event-container';
    mainContentContainer.appendChild(this.eventContainer);
    
    // 创建选项容器
    this.choicesContainer = document.createElement('div');
    this.choicesContainer.className = 'choices-container';
    mainContentContainer.appendChild(this.choicesContainer);

    // 将主内容容器添加到游戏屏幕
    this.gameScreen.appendChild(mainContentContainer);

    // 创建结果容器（初始隐藏）
    this.resultContainer = document.createElement('div');
    this.resultContainer.className = 'result-container hidden';
    this.gameScreen.appendChild(this.resultContainer);
  }

  /**
   * 更新加载进度
   * @param progress 进度值（0-100）
   */
  updateLoadingProgress(progress: number): void {
    if (this.loadingProgress) {
      this.loadingProgress.style.width = `${progress}%`;
    }
  }

  /**
   * 显示开始游戏界面
   */
  showStartScreen(): void {
    // 清空加载屏幕内容
    this.loadingScreen.innerHTML = '';
    
    // 创建标题和开始按钮
    const title = document.createElement('h1');
    title.className = 'hand-drawn';
    title.textContent = '上班路上';
    
    const subtitle = document.createElement('p');
    subtitle.className = 'subtitle';
    subtitle.textContent = '打工人的日常冒险';

    // 创建目标描述元素
    const goalDescriptionElement = document.createElement('div');
    goalDescriptionElement.id = 'goal-description';
    goalDescriptionElement.className = 'goal-description'; // 使用CSS中定义的样式
    const randomIndex = Math.floor(Math.random() * this.goalDescriptions.length);
    goalDescriptionElement.textContent = this.goalDescriptions[randomIndex];
    
    // 创建难度选择区域
    const difficultyContainer = document.createElement('div');
    difficultyContainer.className = 'difficulty-container';
    
    const difficultyTitle = document.createElement('p');
    difficultyTitle.className = 'difficulty-title';
    difficultyTitle.textContent = '选择上班难度';
    difficultyContainer.appendChild(difficultyTitle);
    
    // 创建简单模式按钮
    const easyButton = document.createElement('button');
    easyButton.className = 'hand-drawn-button difficulty-button';
    easyButton.textContent = '简单模式';
    easyButton.onclick = () => {
      this.showDifficultyMessage('想得美！上班路没有简单的！');
    };
    difficultyContainer.appendChild(easyButton);
    
    // 创建正常模式按钮
    const normalButton = document.createElement('button');
    normalButton.className = 'hand-drawn-button difficulty-button';
    normalButton.textContent = '正常模式';
    normalButton.onclick = () => {
      this.showDifficultyMessage('怎么可能有正常的上班路？');
    };
    difficultyContainer.appendChild(normalButton);
    
    // 创建困难模式按钮
    const hardButton = document.createElement('button');
    hardButton.className = 'hand-drawn-button difficulty-button';
    hardButton.textContent = '困难模式';
    hardButton.onclick = () => {
      this.showDifficultyMessage('你还在上班就已经够困难了，就不要折磨自己了号码？');
    };
    difficultyContainer.appendChild(hardButton);
    
    // 添加到加载屏幕
    this.loadingScreen.appendChild(title);
    this.loadingScreen.appendChild(subtitle);
    this.loadingScreen.appendChild(goalDescriptionElement); // 添加目标描述
    this.loadingScreen.appendChild(difficultyContainer);
  }
  
  /**
   * 显示难度选择提示消息，然后开始游戏
   * @param message 提示消息
   */
  private showDifficultyMessage(message: string): void {
    // 先移除已有的消息容器（如果存在）
    const existingContainer = this.loadingScreen.querySelector('.message-container');
    if (existingContainer) {
      this.loadingScreen.removeChild(existingContainer);
    }
    
    // 创建消息容器
    const messageContainer = document.createElement('div');
    messageContainer.className = 'message-container';
    
    // 创建消息文本
    const messageText = document.createElement('p');
    messageText.className = 'message-text';
    messageText.textContent = message;
    messageContainer.appendChild(messageText);
    
    // 添加确认按钮
    const confirmButton = document.createElement('button');
    confirmButton.className = 'hand-drawn-button';
    confirmButton.textContent = '开始通勤';
    confirmButton.id = 'start-game-button';
    
    // 使用onclick而不是addEventListener
    confirmButton.onclick = () => {
      // 移除消息容器
      if (messageContainer && messageContainer.parentNode) {
        messageContainer.parentNode.removeChild(messageContainer);
      }
      
      // 直接调用开始游戏
      if (this.onGameStartCallback) {
        this.onGameStartCallback();
      }
    };
    
    messageContainer.appendChild(confirmButton);
    
    // 添加到加载屏幕
    this.loadingScreen.appendChild(messageContainer);
    
    // 尝试直接为按钮添加点击处理器
    setTimeout(() => {
      const startButton = document.getElementById('start-game-button');
      if (startButton) {
        startButton.onclick = () => {
          if (this.onGameStartCallback) {
            this.onGameStartCallback();
          }
        };
      }
    }, 100);
  }

  /**
   * 显示游戏结束界面
   * @param state 最终游戏状态
   * @param endingType 结局类型
   * @param specialEndingTitle 特殊结局标题（可选）
   * @param specialEndingDescription 特殊结局描述（可选）
   */
  showGameOverScreen(
    state: GameState, 
    endingType: EndingType, 
    specialEndingTitle?: string, 
    specialEndingDescription?: string
  ): void {
    if (!this.gameScreen) return;
    
    // 清空游戏屏幕
    this.gameScreen.innerHTML = '';
    
    // 重置游戏屏幕的样式为结局屏幕样式
    this.gameScreen.style.display = 'flex';
    this.gameScreen.style.flexDirection = 'column';
    this.gameScreen.style.justifyContent = 'flex-start'; // 改为从顶部开始
    this.gameScreen.style.alignItems = 'center';
    this.gameScreen.style.padding = '20px';
    this.gameScreen.style.textAlign = 'center';
    this.gameScreen.style.overflow = 'auto'; // 允许滚动
    this.gameScreen.style.height = '100%'; // 确保占满容器
    
    // 创建滚动容器
    const scrollContainer = document.createElement('div');
    scrollContainer.style.width = '100%';
    scrollContainer.style.maxWidth = '800px';
    scrollContainer.style.display = 'flex';
    scrollContainer.style.flexDirection = 'column';
    scrollContainer.style.alignItems = 'center';
    scrollContainer.style.gap = '20px';
    scrollContainer.style.paddingBottom = '40px'; // 底部留出空间
    
    // --- 修改结局标题和描述的获取方式 ---
    let finalTitle = this.getEndingTitle(endingType); // 默认标题
    let finalDescription = '';
    let imageName: string | null = null; // 用于存储图片文件名

    if (endingType === EndingType.SPECIAL && specialEndingDescription) {
      // 如果是特殊结局，优先使用传递过来的特殊结局标题
      if (specialEndingTitle) {
        finalTitle = specialEndingTitle;
        imageName = `${specialEndingTitle}.jpg`; // 尝试使用特殊标题作为图片名
      }
      
      // 获取基础的时间状态描述
      const timeStatusDescription = this.getTimeStatusDescription(state);
      
      // 组合描述
      finalDescription = `${timeStatusDescription} 而且，${specialEndingDescription}`;
      
    } else {
      // 对于非特殊结局，使用原来的逻辑
      finalDescription = this.getEndingDescription(endingType, state);
      // 为普通结局确定图片名
      const endingTypeString = EndingType[endingType]; // 获取枚举的字符串表示
      if (endingTypeString) {
        imageName = `EndingType.${endingTypeString}.jpg`;
      }
    }
    // --- 结束修改 ---

    // 创建结束标题
    const titleElement = document.createElement('h1');
    titleElement.className = 'hand-drawn';
    titleElement.textContent = finalTitle;
    titleElement.style.margin = '0 0 20px 0';
    
    // 创建结束描述
    const descriptionElement = document.createElement('p');
    descriptionElement.className = 'ending-description';
    descriptionElement.textContent = finalDescription;
    
    // 添加标题和描述到滚动容器
    scrollContainer.appendChild(titleElement);
    scrollContainer.appendChild(descriptionElement);

    // --- 添加结局图片 ---
    if (imageName) {
      const imagePath = `images/endings/${imageName}`; // 路径相对于部署后的根目录
      const endingImageElement = document.createElement('img');
      endingImageElement.src = imagePath;
      endingImageElement.alt = finalTitle; // 使用最终标题作为alt文本
      endingImageElement.className = 'ending-image';
      
      // 尝试加载图片，如果失败则不显示 (可选的错误处理)
      endingImageElement.onerror = () => {
        if (endingImageElement.parentNode) {
          endingImageElement.parentNode.removeChild(endingImageElement);
        }
      };
      scrollContainer.appendChild(endingImageElement);
    }
    // --- 结束添加 ---
    
    // 创建统计信息
    const stats = document.createElement('div');
    stats.className = 'game-stats';
    
    const timeText = this.formatTime(state.currentTime);
    const distance = state.totalDistance - state.distanceToWork;
    
    stats.innerHTML = `
      <p>最终时间: <span>${timeText}</span></p>
      <p>行程距离: <span>${distance.toFixed(1)}公里</span></p>
      <p>遭遇事件: <span>${state.eventsEncountered.size}个</span></p>
    `;
    scrollContainer.appendChild(stats);
    
    // 创建按钮容器
    const buttonContainer = document.createElement('div');
    buttonContainer.style.display = 'flex';
    buttonContainer.style.flexDirection = 'column';
    buttonContainer.style.gap = '15px';
    buttonContainer.style.alignItems = 'center';
    buttonContainer.style.width = '100%';
    buttonContainer.style.maxWidth = '300px';
    
    // 创建重新开始按钮
    const restartButton = document.createElement('button');
    restartButton.className = 'hand-drawn-button';
    restartButton.textContent = '重新上班';
    restartButton.style.width = '100%';
    restartButton.style.padding = '15px 20px';
    restartButton.style.fontSize = '1.2rem';
    restartButton.addEventListener('click', () => {
      if (this.onGameRestartCallback) {
        this.onGameRestartCallback();
      }
    });
    
    // 添加"提交意见"链接
    const feedbackLink = document.createElement('a');
    feedbackLink.id = 'feedbackLink';
    feedbackLink.href = 'mailto:hard2work@163.com?subject=上班路上 - 游戏反馈';
    feedbackLink.textContent = '提交意见';
    feedbackLink.target = '_blank'; // 尝试在新标签页打开，尽管mailto通常直接调用客户端
    feedbackLink.style.width = '100%';
    feedbackLink.style.textAlign = 'center';
    feedbackLink.style.padding = '12px 20px';
    feedbackLink.style.fontSize = '1.1rem';
    feedbackLink.style.display = 'block';
    feedbackLink.style.textDecoration = 'none';
    
    // 添加按钮到按钮容器
    buttonContainer.appendChild(restartButton);
    buttonContainer.appendChild(feedbackLink);
    
    // 添加按钮容器到滚动容器
    scrollContainer.appendChild(buttonContainer);
    
    // 将滚动容器添加到游戏屏幕
    this.gameScreen.appendChild(scrollContainer);

    // 如果是提前或准时到达，则显示庆祝特效
    const workStartTime = 540; // 9:00 AM. TODO: Ideally, get this from GameConfig if accessible

    if (endingType === EndingType.EARLY || 
        endingType === EndingType.ON_TIME ||
        (endingType === EndingType.SPECIAL && state.currentTime <= workStartTime)
    ) {
      this.renderCelebrationEffect(this.gameScreen);
    }
  }

  /**
   * 获取结局标题
   * @param endingType 结局类型
   */
  private getEndingTitle(endingType: EndingType): string {
    switch (endingType) {
      case EndingType.EARLY:
        return '提前到达！';
      case EndingType.ON_TIME:
        return '准时到达！';
      case EndingType.LATE:
        return '迟到了...';
      case EndingType.VERY_LATE:
        return '严重迟到！';
      case EndingType.NEVER:
        return '永远到不了公司...';
      default:
        return '游戏结束';
    }
  }

  /**
   * 获取结局描述
   * @param endingType 结局类型
   * @param state 游戏状态
   */
  private getEndingDescription(endingType: EndingType, state: GameState): string {
    switch (endingType) {
      case EndingType.EARLY:
        return `你提前${Math.round((540 - state.currentTime) / 5) * 5}分钟到达了公司，老板对你的积极性表示赞赏。不过，这意味着你又要多干活了...`;
      case EndingType.ON_TIME:
        return '你正好准时到达公司，完美地融入了朝九晚五的打工生活。';
      case EndingType.LATE:
        return `你迟到了${Math.round((state.currentTime - 540) / 5) * 5}分钟，老板给了你一个不满的眼神，但似乎没有太在意。`;
      case EndingType.VERY_LATE:
        return `你严重迟到了${Math.round((state.currentTime - 540) / 5) * 5}分钟，老板已经在你的工位旁边站了半天，看起来很不高兴。`;
      case EndingType.NEVER:
        return '你永远到不了公司了...今天的上班之路实在太艰难，也许这是命运给你的暗示？';
      default:
        return '游戏结束了，你的上班之路充满了各种意外。';
    }
  }

  /**
   * 新增：根据状态获取基础时间状态描述（早到/准时/迟到）
   * @param state 游戏状态
   */
  private getTimeStatusDescription(state: GameState): string {
    const arrivalTime = state.currentTime;
    // TODO: 获取正确的上班时间，暂时硬编码为 540 (9:00 AM)
    const targetTime = 540; 
    const timeDiff = arrivalTime - targetTime;

    if (timeDiff < 0) { // 修改为 timeDiff < 0 专门处理早到
      return `你提前${targetTime - arrivalTime}分钟到达了公司。`;
    } else if (timeDiff === 0) { // 单独处理准时
      return '你正好准时到达公司。';
    } else if (timeDiff <= 5) { // 之前这个分支包含了准时，现在是迟到 <=5 分钟
      // 如果还想对迟到时间做处理，例如 Math.round(timeDiff / 5) * 5，可以在这里加
      // 为了精确，我们先直接用 timeDiff
      return `你迟到了${timeDiff}分钟。`; // 或者使用之前的归整逻辑 `你迟到了${Math.round(timeDiff / 5) * 5}分钟。`
    } else if (timeDiff <= 15) {
      return `你迟到了${Math.round(timeDiff / 5) * 5}分钟。`;
    } else {
      return `你严重迟到了${Math.round(timeDiff / 5) * 5}分钟！`;
    }
  }

  /**
   * 渲染事件
   * @param event 事件对象
   */
  renderEvent(event: Event): void {
    if (!this.eventContainer || !this.choicesContainer) {
      return;
    }
    
    // 不隐藏结果容器，让它自然淡出或保持显示
    
    // 清空容器
    this.eventContainer.innerHTML = '';
    this.choicesContainer.innerHTML = '';
    
    // 创建事件标题
    const title = document.createElement('h2');
    title.className = 'event-title hand-drawn';
    title.textContent = event.title;
    
    // 创建事件描述
    const description = document.createElement('p');
    description.className = 'event-description';
    description.textContent = event.description;
    
    // 如果有图片，添加图片
    if (event.imageUrl) {
      const image = document.createElement('img');
      image.className = 'event-image';
      image.src = event.imageUrl;
      image.alt = event.title;
      this.eventContainer.appendChild(image);
    }
    
    // 添加标题和描述
    this.eventContainer.appendChild(title);
    this.eventContainer.appendChild(description);
    
    // 渲染选项
    this.renderChoices(event);
  }

  /**
   * 渲染选项
   * @param event 事件对象
   */
  private renderChoices(event: Event): void {
    if (!this.choicesContainer) return;
    
    const choicesList = document.createElement('div');
    choicesList.className = 'choices-list';
    
    // 统一使用单列布局，不再根据选项数量区分
    choicesList.classList.add('grid-single-col');
    
    event.choices.forEach(choice => {
      const choiceButton = document.createElement('button');
      choiceButton.className = 'choice-button hand-drawn-button';
      choiceButton.dataset.choiceId = choice.id;
      choiceButton.textContent = choice.text;
      
      choiceButton.addEventListener('click', () => {
        if (this.onChoiceSelectedCallback) {
          this.onChoiceSelectedCallback(choice.id);
        }
      });
      
      choicesList.appendChild(choiceButton);
    });
    
    this.choicesContainer.appendChild(choicesList);
  }

  /**
   * 更新状态栏
   * @param state 游戏状态
   * @param result 选择结果（可选）
   * @param skipMessageUpdate 是否跳过消息覆盖层更新（可选，默认为false）
   */
  updateStatusBar(state: GameState, result?: EventResult, skipMessageUpdate: boolean = false): void {
    if (!this.statusBar) return;

    const timeText = this.formatTime(state.currentTime);
    const progress = Math.max(0, (1 - state.distanceToWork / state.totalDistance)) * 100;

    let timeContainer = this.statusBar.querySelector<HTMLElement>('.status-time');
    let distanceContainer = this.statusBar.querySelector<HTMLElement>('.status-distance');
    
    // 时间容器
    if (!timeContainer) {
      timeContainer = document.createElement('div');
      timeContainer.className = 'status-time';
      this.statusBar.appendChild(timeContainer);
    }
    timeContainer.innerHTML = ''; 
    const timeLabelValueContainer = document.createElement('span');
    timeLabelValueContainer.className = 'status-label-value';
    const timeLabel = document.createElement('span');
    timeLabel.className = 'time-label';
    timeLabel.textContent = '时间:';
    const timeValue = document.createElement('span');
    timeValue.className = 'time-value hand-drawn';
    timeValue.textContent = timeText;
    timeLabelValueContainer.appendChild(timeLabel);
    timeLabelValueContainer.appendChild(timeValue);
    timeContainer.appendChild(timeLabelValueContainer);

    // 距离容器
    if (!distanceContainer) {
      distanceContainer = document.createElement('div');
      distanceContainer.className = 'status-distance';
      this.statusBar.appendChild(distanceContainer);
    }
    distanceContainer.innerHTML = ''; 
    const distanceLabelValueContainer = document.createElement('span');
    distanceLabelValueContainer.className = 'status-label-value';
    const distanceLabel = document.createElement('span');
    distanceLabel.className = 'distance-label';
    distanceLabel.textContent = '距离公司:';
    const distanceValue = document.createElement('span');
    distanceValue.className = 'distance-value hand-drawn';
    distanceValue.textContent = `${state.distanceToWork.toFixed(1)}公里`;
    distanceLabelValueContainer.appendChild(distanceLabel);
    distanceLabelValueContainer.appendChild(distanceValue);
    distanceContainer.appendChild(distanceLabelValueContainer);
    
    // 进度条容器 (新的实现方式)
    if (!this.progressContainerElement) {
      this.progressContainerElement = document.createElement('div');
      this.progressContainerElement.className = 'status-progress';
      this.statusBar.appendChild(this.progressContainerElement);

      this.homeIconElement = document.createElement('span');
      this.homeIconElement.className = 'home-icon';
      this.homeIconElement.textContent = '家';
      this.progressContainerElement.appendChild(this.homeIconElement);

      // routeLine 将在CSS中通过伪元素或绝对定位创建，或作为personIcon的背景
      // 为了简单起见，我们先创建一个实际的routeLine元素，并用CSS定位
      this.routeLineElement = document.createElement('div');
      this.routeLineElement.className = 'route-line';
      this.progressContainerElement.appendChild(this.routeLineElement); 

      this.personIconElement = document.createElement('span');
      this.personIconElement.className = 'person-icon';
      this.personIconElement.textContent = '你'; 
      this.progressContainerElement.appendChild(this.personIconElement);
      
      this.destinationIconElement = document.createElement('span');
      this.destinationIconElement.className = 'destination-icon';
      this.destinationIconElement.textContent = '班';
      this.progressContainerElement.appendChild(this.destinationIconElement);
    }

    // 更新 "你" 图标的位置
    // 使用 requestAnimationFrame 来确保在计算尺寸之前DOM已更新
    requestAnimationFrame(() => {
        if(this.progressContainerElement && this.homeIconElement && this.destinationIconElement && this.personIconElement && this.routeLineElement) {
            const containerWidth = this.progressContainerElement.offsetWidth;
            const homeIconWidth = this.homeIconElement.offsetWidth;
            const destIconWidth = this.destinationIconElement.offsetWidth;
            //const personIconWidth = this.personIconElement.offsetWidth;

            // 定义 "你" 图标可移动的有效轨迹的起始和结束点（相对于progressContainerElement）
            // 起点大约是 "家" 图标的右边缘，终点大约是 "班" 图标的左边缘
            // 留出一些padding，让人图标不会完全贴合家和班图标
            const padding = 10; // px, 家/班图标与轨迹末端的间距
            const effectiveTrackStartX = homeIconWidth + padding;
            const effectiveTrackEndX = containerWidth - destIconWidth - padding;
            const effectiveTrackWidth = effectiveTrackEndX - effectiveTrackStartX;
            
            if (effectiveTrackWidth > 0) {
                // 计算 "你" 图标的左边缘应该在的位置
                // progress 是 0-100 的值
                //let personTargetLeft = effectiveTrackStartX + (progress / 100) * effectiveTrackWidth;
                
                // 为了让 "你" 图标的中心点大致对应进度，从目标左边缘减去图标宽度的一半
                // 但这里我们希望图标的左边缘作为定位点，同时其中心大致对准进度
                // 也可以简化为，让图标的中心点对准进度点：
                // personTargetLeft = effectiveTrackStartX + (progress / 100) * effectiveTrackWidth - (personIconWidth / 2);
                // 我们选择让图标的定位点（其CSS的left属性）是其视觉中心
                // 因此，我们计算的是其中心点应该在的位置
                let personCenterTargetX = effectiveTrackStartX + (progress / 100) * effectiveTrackWidth;

                this.personIconElement.style.left = `${personCenterTargetX}px`;
            } else {
                // 如果轨迹宽度过小，则将人放在中间或起点
                this.personIconElement.style.left = `${effectiveTrackStartX}px`;
            }
        }
    });
    
    // 添加状态栏CSS样式 (确保只添加一次)
    if (!document.getElementById('status-bar-styles')) {
      this.addStatusBarStyles();
    }

    // 更新消息覆盖层 (Conditionally)
    if (!skipMessageUpdate) {
      this.updateMessageOverlay(result);
    }
  }

  /**
   * 更新消息覆盖层的内容
   * @param result 选择结果（可选）
   */
  private updateMessageOverlay(result?: EventResult): void {
    if (!this.persistentMessageElement) return;

    const messageElement = this.persistentMessageElement;
    let textScroller = messageElement.querySelector<HTMLElement>('.text-scroller');

    // 如果 textScroller 不存在，则创建它
    if (!textScroller) {
      textScroller = document.createElement('p');
      textScroller.className = 'text-scroller';
      messageElement.appendChild(textScroller);
    }

    // 先移除动画相关的类和样式，以便重新计算和触发
    textScroller.classList.remove('scrolling-active');
    textScroller.style.animationDuration = '';
    textScroller.style.removeProperty('--scroll-translate-y');
    textScroller.style.transform = 'translateY(0)';

    if (result && result.resultMessage) {
      let fullMessage = result.resultMessage;
      const changes: string[] = [];

      if (result.timeChange !== 0) {
        changes.push(`时间${result.timeChange > 0 ? '+' : ''}${result.timeChange}分钟`);
      }
      if (result.distanceChange !== 0) {
        const distanceSign = result.distanceChange < 0 ? "" : "+";
        changes.push(`距离公司${distanceSign}${result.distanceChange.toFixed(1)}公里`);
      }
      if (changes.length > 0) {
        fullMessage += ` (${changes.join('，')})`;
      }
      if (result.specialEffect && result.specialEffect.description) {
        fullMessage += ` [效果: ${result.specialEffect.description}]`;
      }

      textScroller.textContent = fullMessage;
      messageElement.classList.remove('hidden');

      // 强制浏览器重新计算布局和样式
      //const _ = textScroller.offsetHeight;

      // 检查是否溢出并应用滚动动画
      if (textScroller.scrollHeight > messageElement.clientHeight) {
        const scrollAmount = -(textScroller.scrollHeight - messageElement.clientHeight);
        const extraScroll = 5;
        const finalScrollAmount = scrollAmount - extraScroll;

        textScroller.style.setProperty('--scroll-translate-y', `${finalScrollAmount}px`);

        const scrollableHeight = Math.abs(finalScrollAmount);
        let duration = Math.round(scrollableHeight / 15) + 2;
        duration = Math.max(4, Math.min(duration, 10));
        
        textScroller.style.animationDuration = `${duration}s`;
        textScroller.classList.add('scrolling-active');
      }
    } else {
      textScroller.textContent = '';
      messageElement.classList.add('hidden');
    }
  }

  /**
   * 添加状态栏的CSS样式
   */
  private addStatusBarStyles(): void {
    // 检查是否已经添加了样式
    if (document.getElementById('status-bar-styles')) return;
    
    const styleElement = document.createElement('style');
    styleElement.id = 'status-bar-styles';
    // 全局高度设定提示 (通常在 main.css 或 index.html <style> 中完成)
    // styleElement.textContent += `
    //   html, body, #app { /* #app 是 this.gameContainer 的例子 */
    //     height: 100%;
    //     margin: 0;
    //     padding: 0;
    //     overflow: hidden; /* 防止body滚动，让内部控制 */
    //   }
    // `;

    styleElement.textContent += `
      /* .game-screen 在 initializeGameUI 中通过内联样式设置了flex布局 */
      /* 如果没有，可以在这里添加：
      .screen#game-screen {
        display: flex;
        flex-direction: column;
        height: 100%; 
      } 
      */

      .status-bar {
        /* ... existing styles ... */
        flex-shrink: 0; /* 防止被压缩 */
      }
      
      .message-overlay { 
        /* ... existing styles ... */
        flex-shrink: 0; /* 高度固定，不压缩 */
        position: relative; /* 确保 z-index 生效，如果需要覆盖 */
        z-index: 10; /* 确保在 event-container 之上，如果它们可能重叠 */
      }

      .event-container {
        /* className: event-container */
        flex-shrink: 0; /* 不压缩，根据内容自适应高度 */
        overflow-y: auto; /* 内容超出时垂直滚动 */
        min-height: 100px; /* 保证一个最小高度 */
        max-height: 40vh; /* 限制最大高度，避免占用过多空间 */
        padding: 10px 15px; /* 内部内容边距 */
        border-top: 1px solid #eee; /* 与上方元素的视觉分隔 */
        border-bottom: 1px solid #eee; /* 与下方选项的视觉分隔 */
        background-color: #fff; /* 背景色，以防透明看到下方 */
      }

      .choices-container {
        /* className: choices-container */
        flex: 1; /* 占据剩余空间，让选项有足够显示空间 */
        overflow-y: auto; /* 如果选项很多才滚动 */
        padding: 10px 15px;
        background-color: #f9f9f9; /* 轻微背景区分 */
        min-height: 150px; /* 确保选项有足够的最小高度 */
      }

      /* 移动端优化空间分配 */
      @media (max-width: 768px) {
        .event-container {
          max-height: 30vh !important; /* 移动端进一步限制事件容器高度 */
          padding: 8px 12px !important;
        }
        
        .choices-container {
          min-height: 200px !important; /* 移动端选项容器更大的最小高度 */
          padding: 8px 12px !important;
        }
      }

      /* 已有的 .status-bar 内部样式 */
      .status-bar {
        display: grid;
        grid-template-columns: 1fr 1fr; /* 两列布局 */
        grid-template-rows: auto auto; /* 时间和距离占第一行，进度条占第二行 */
        grid-template-areas:
          "time distance"
          "progress progress"; /* 进度条区域跨越两列 */
        grid-gap: 5px 10px; /* 行间距5px，列间距10px */
        background-color: rgba(255, 255, 255, 0.9);
        border-bottom: 2px solid #333;
        padding: 10px 15px;
        overflow: hidden; 
      }
      
      .status-time {
        grid-area: time; /* 分配到grid区域 */
        display: flex;
        align-items: center;
        font-size: 16px;
        white-space: nowrap;
      }
      
      .status-distance {
        grid-area: distance; /* 分配到grid区域 */
        display: flex;
        align-items: center;
        justify-content: flex-end; /* 距离靠右对齐 */
        font-size: 16px;
        white-space: nowrap;
      }
      
      .status-label-value { 
        display: inline-flex; 
        align-items: baseline; 
        /* flex-grow: 1; */ /* 移除，避免过度拉伸 */
      }
      
      .time-label, .distance-label {
        font-weight: bold;
        margin-right: 6px;
      }
      
      .time-value, .distance-value {
        font-family: 'Courier New', monospace;
      }
      
      .status-progress {
        grid-area: progress; 
        display: flex; /* 使用flex布局来排列家、(隐含的线)、班 */
        align-items: center; /* 垂直居中flex子项 */
        justify-content: space-between; /* 家和班分别在两端 */
        width: 100%; 
        position: relative; /* 用于内部绝对定位的 person-icon 和 route-line */
        padding: 10px 0; 
        height: 50px; /* 明确高度 */
      }

      .home-icon, .destination-icon {
        font-size: 1.5em; 
        font-weight: bold;
        color: #333;
        padding: 0 5px; 
        /* 他们是flex items, 不需要绝对定位 */
        z-index: 1; /* 确保在家/班图标在线和人之上，如果重叠 */
      }
      
      .route-line {
        position: absolute;
        height: 3px; /* 线条高度 */
        background-color: #555; 
        bottom: 18px; /* 定位在线条的垂直位置，让"你"的脚部可以踩在上面 */
        left: 30px; /* 留出"家"图标的空间，需要JS动态计算或更精确的CSS */
        right: 30px; /* 留出"班"图标的空间 */
        z-index: 0; /* 线条在最底层 */
        border-radius: 1.5px;
      }

      .person-icon {
        position: absolute;
        font-size: 1.6em; 
        color: #e74c3c; 
        bottom: 18px; /* "你"的脚部与线条顶部对齐或略高于线条 */
        transform: translateX(-50%); /* 使left属性代表其水平中心点 */
        transition: left 0.5s ease-out; 
        z-index: 1; /* "你"在线条之上 */
        white-space: nowrap; 
      }
      
      .overlay-message { /* 这是 messageElement */
        font-size: 14px;
        color: #333;
        background-color: rgba(255, 255, 200, 0.95); 
        padding: 5px 10px;
        border-radius: 4px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        width: 100%;    /* 尝试填满父容器的可用宽度 (父容器的padding会提供边缘空隙) */
        max-width: 100%; /* 允许它填满父容器的可用宽度 */
        max-height: 4.5em; /* 增加高度，从2.8em改为4.5em，可以显示更多文本 */
        overflow: hidden; 
      }

      /* 移动端overlay-message优化 */
      @media (max-width: 768px) {
        .overlay-message {
          max-height: 5em !important; /* 移动端更大的高度，因为现在有更多空间 */
          font-size: 13px !important; /* 稍微调整字体大小以适应更多内容 */
          padding: 8px 12px !important; /* 增加padding以获得更好的阅读体验 */
        }
      }
      
      .text-scroller {
          display: block; /* 改为block，方便计算scrollHeight */
          width: 100%;
          text-align: center; /* 文字内容居中 */
          /* 动画相关的属性会在scrolling-active类中定义 */
          will-change: transform; /* 性能提示 */
      }

      .text-scroller.scrolling-active {
          animation-name: scrollTextUp;
          animation-timing-function: linear;
          animation-iteration-count: 1; 
          animation-fill-mode: forwards; 
          /* animation-duration 通过JS动态设置 */
      }

      @keyframes scrollTextUp {
          0% {
              transform: translateY(0);
          }
          15% { /* 调整停留时间比例 */
              transform: translateY(0);
          }
          85% {
              transform: translateY(var(--scroll-translate-y, -20px)); 
          }
          100% {
              transform: translateY(var(--scroll-translate-y, -20px));
          }
      }

      /* 庆祝特效样式 */
      .celebration-particle {
        position: absolute;
        bottom: 0;
        left: 50%; /* 水平居中 */
        opacity: 0;
        /* width/height/backgroundColor/borderRadius/animationDelay 在JS中设置 */
        animation: particleExplosion 1.8s ease-out forwards;
        pointer-events: none; /* 确保粒子不会干扰点击 */
        will-change: transform, opacity;
      }

      @keyframes particleExplosion {
        0% {
          opacity: 1;
          transform: translate(-50%, 0) scale(0.5); /* 从底部中心开始，较小 */
        }
        20% {
          opacity: 1;
          /* 可以在这里添加一个短暂的向上直升阶段 */
        }
        100% {
          opacity: 0;
          transform: translate(calc(-50% + var(--particle-random-x)), -85vh) scale(1.2); /* 向上并散开，变大然后消失 */
        }
      }

      .ending-image {
        max-width: 80%;
        max-height: 300px; 
        display: block; 
        margin: 20px auto; 
        border: 3px solid #555;
        border-radius: 10px;
        box-shadow: 0 4px 8px rgba(0,0,0,0.2);
      }

      .effect-note {
        font-style: italic;
        color: #0066cc;
      }

      /* REMOVE .change-indicator styles */
      /*
      .change-indicator {
        position: relative;
        margin-left: auto; // Push indicator to the right
        flex-shrink: 0; // Prevent indicator from shrinking
        font-weight: bold;
        // animation: flashEffect 2.5s ease-in-out; // Remove animation
        // animation-fill-mode: forwards;
        font-size: 14px;
        opacity: 0; // Default hidden
        transition: opacity 0.5s ease-in-out; // Add transition
      }

      .change-indicator.active {
        opacity: 1; // Visible when active
      }

      .change-indicator.positive {
        color: green;
      }

      .change-indicator.negative {
        color: red;
      }
      */

      /* @keyframes flashEffect { */ /* Remove keyframes */
      /*   0% { opacity: 0; } */
      /*   10% { opacity: 1; } */
      /*   70% { opacity: 1; } */
      /*   100% { opacity: 0; } */
      /* } */

      /* Utility class to hide elements */
      /* .hidden { */ /* Keep hidden class definition */
      /*   display: none !important; */
      /* } */

      #feedbackLink {
        display: block; /* 或 inline-block */
        margin-top: 20px;
        padding: 10px 15px;
        background-color: #4CAF50; /* 一个示例颜色，可以调整 */
        color: white;
        text-decoration: none;
        border-radius: 5px;
        text-align: center;
        font-family: var(--hand-drawn-font, 'ZCOOL KuaiLe', sans-serif);
        font-size: 1.2em;
        border: 2px solid #333; /* 与 hand-drawn-button 类似 */
        box-shadow: 2px 2px 0px #888; /* 与 hand-drawn-button 类似 */
        cursor: pointer;
      }

      #feedbackLink:hover {
        background-color: #45a049;
      }

      #feedbackLink:active {
        transform: translateY(1px) translateX(1px); /* 与 hand-drawn-button 类似 */
        box-shadow: 1px 1px 0px #888; /* 与 hand-drawn-button 类似 */
      }
    `;

    // Check if document.head exists before appending
    const head = document.head;
    if (head) {
      head.appendChild(styleElement);
    }
  }

  /**
   * 格式化时间（分钟转为小时:分钟）
   * @param minutes 总分钟数
   */
  private formatTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  /**
   * 显示屏幕
   * @param screen 屏幕元素
   */
  showScreen(screen: HTMLElement): void {
    screen.classList.remove('hidden');
  }

  /**
   * 隐藏屏幕
   * @param screen 屏幕元素
   */
  hideScreen(screen: HTMLElement): void {
    screen.classList.add('hidden');
  }

  /**
   * 切换到游戏屏幕
   */
  showGameScreen(): void {
    this.hideScreen(this.loadingScreen);
    this.showScreen(this.gameScreen);
  }

  /**
   * 切换到加载屏幕
   */
  showLoadingScreen(): void {
    this.hideScreen(this.gameScreen);
    this.showScreen(this.loadingScreen);
  }

  /**
   * 设置选项选择回调
   * @param callback 回调函数
   */
  onChoiceSelected(callback: (choiceId: string) => void): void {
    this.onChoiceSelectedCallback = callback;
  }

  /**
   * 设置游戏开始回调
   * @param callback 回调函数
   */
  onGameStart(callback: () => void): void {
    this.onGameStartCallback = callback;
  }

  /**
   * 设置游戏重新开始回调
   * @param callback 回调函数
   */
  onGameRestart(callback: () => void): void {
    this.onGameRestartCallback = callback;
  }

  /**
   * 渲染庆祝特效 (烟花/礼炮)
   * @param container 特效渲染的容器元素
   */
  private renderCelebrationEffect(container: HTMLElement): void {
    const particleCount = 50;
    const colors = ['#FFD700', '#FF6347', '#00BFFF', '#32CD32', '#FF69B4', '#ADFF2F']; 

    for (let i = 0; i < particleCount; i++) {
      const particle = document.createElement('div');
      particle.className = 'celebration-particle';
      
      particle.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
      const size = Math.random() * 10 + 5; 
      particle.style.width = `${size}px`;
      particle.style.height = `${size}px`;
      particle.style.borderRadius = Math.random() > 0.5 ? '50%' : '0'; 
      particle.style.animationDelay = `${Math.random() * 1}s`; 
      const randomX = (Math.random() - 0.5) * 80; 
      particle.style.setProperty('--particle-random-x', `${randomX}vw`);

      container.appendChild(particle);

      particle.addEventListener('animationend', () => {
        if (particle.parentNode) {
          particle.parentNode.removeChild(particle);
        }
      });
    }
  }
} 